import React, { forwardRef, memo, useImperativeHandle } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import {
  createSKU,
  createCaseSKU,
  createEquipmentSKU,
  createPackageSKU,
} from 'api/sku';
import { useMutation, useQueryClient } from 'react-query';

import { successToast } from 'utils/successToast';

import { RefType, AddSkuModalProps } from './AddSkuModal.props';

import AddSkuModalView from './AddSkuModal.view';
import { toManufacturerDetailsPayload } from 'components/modules/SkuManufacturerDetails';

const Container = forwardRef<RefType, AddSkuModalProps>((props, ref) => {
  const queryClient = useQueryClient();

  const { type } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();

  useImperativeHandle(
    ref,
    () => ({
      onOpen,
    }),
    [],
  );

  const invalidateSkuQueries = () => {
    queryClient.invalidateQueries(`fetch-all-sku-${type}`);
    queryClient.invalidateQueries({
      queryKey: [`fetch-all-sku-select`],
    });
    queryClient.invalidateQueries(['fetch-skus-by-manufacturer']);
  };

  const { mutate: createSku, isLoading } = useMutation(createSKU, {
    onSuccess: () => {
      successToast(`SKU ${type} created successfully`);
      invalidateSkuQueries();
      onClose();
    },
  });

  const { mutate: createCaseSku, isLoading: isLoadingSku } = useMutation(
    createCaseSKU,
    {
      onSuccess: () => {
        successToast(`SKU ${type} created successfully`);
        invalidateSkuQueries();
        onClose();
      },
    },
  );

  const {
    mutate: createPackageSku,
    isLoading: isLoadingCreatePackage,
  } = useMutation(createPackageSKU, {
    onSuccess: () => {
      successToast(`SKU ${type} created successfully`);
      invalidateSkuQueries();
      onClose();
    },
  });

  const {
    mutate: createEquipmentSku,
    isLoading: isLoadingEquipment,
  } = useMutation(createEquipmentSKU, {
    onSuccess: () => {
      successToast(`SKU ${type} created successfully`);
      invalidateSkuQueries();
      onClose();
    },
  });

  const _props = {
    type,
    isOpen,
    onClose,
    isLoading,
    isLoadingSku,
    isLoadingCreatePackage,
    isLoadingEquipment,
    onCreateEquipmentSku: async (payload: any) => {
      createEquipmentSku({
        name: payload.name,
        category: payload.category,
        // Optional per the ticket: an empty select must not be sent as ''.
        manufacturer: payload.manufacturer || undefined,
      });
    },
    onCreate: async (payload: any) => {
      const formattedPayload = {
        ...payload,
        type,
        weight: payload.weight?.toString() || '',
        prefix: payload.sku,
        size: Number(payload?.size || '0'),
        itemsInCase: Number(payload?.itemsInCase || '0'),
        pricePerDelivery: Number(payload?.pricePerDelivery || '0'),
        pricePerReplacement: Number(payload?.pricePerReplacement || '0'),
        ...toManufacturerDetailsPayload(payload, 'create'),
      };

      createSku(formattedPayload);
    },
    onCreateCaseSku: async (payload: any) => {
      const formattedPayload = {
        ...payload,
        type,
      };

      createCaseSku(formattedPayload);
    },
    onCreatePackageSku: async (payload: any) => {
      const formattedPayload = {
        name: payload.name,
        caseSku: payload.caseSku,
        description: payload?.description,
        items:
          payload?.items?.length > 0
            ? payload?.items?.map((i: any) => ({
                ...i,
                quantity: Number(i.quantity),
              }))
            : undefined,
        washHubs: payload?.washHubs,
      };

      createPackageSku(formattedPayload);
    },
  };

  return <AddSkuModalView {..._props} />;
});

const AddSkuModal = memo(Container);

export default AddSkuModal;
